if (!customElements.get('gallery-zoom-open')) {
  class GalleryZoomOpen extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', this.handleClick.bind(this));
    }

    /**
     * Handle a click event.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
      const mediaGallery = this.closest('media-gallery');
      const zoomDevice = mediaGallery.dataset.zoomEnabled;

      if (zoomDevice.length === 0 || (zoomDevice === 'mobile' && !theme.mediaMatches.md) || (zoomDevice === 'desktop' && theme.mediaMatches.md)) {
        // Add to document on first open
        if (!mediaGallery.galleryModal.parentElement) {
          document.body.appendChild(mediaGallery.galleryModal);
        }

        // Belt-and-braces: if the modal clone wasn't upgraded to <modal-dialog> (no .open method), upgrade it
        // now so the lightbox can open instead of throwing "open is not a function".
        if (typeof mediaGallery.galleryModal.open !== 'function' && window.customElements && customElements.upgrade) {
          customElements.upgrade(mediaGallery.galleryModal);
        }
        if (typeof mediaGallery.galleryModal.open !== 'function') return;

        mediaGallery.galleryModal.open(evt.currentTarget);
        const zoom = mediaGallery.galleryModal.querySelector('gallery-zoom');
        zoom.init(evt.currentTarget.parentElement.dataset.mediaId);
        zoom.focus();

        evt.preventDefault();
      }
    }
  }

  window.customElements.define('gallery-zoom-open', GalleryZoomOpen);
}

/* eslint-disable max-len */

if (!customElements.get('gallery-zoom')) {
  class GalleryZoom extends HTMLElement {
    connectedCallback() {
      if (!this.initialised) {
        this.initialised = true;

        // ui
        this.classList.add('gallery-zoom--pre-reveal');
        this.zoomContainer = this.querySelector('.gallery-zoom__zoom-container');
        this.thumbContainer = this.querySelector('.gallery-zoom__thumbs');
        this.controlsContainer = this.querySelector('.gallery-zoom__controls');
        this.previousBtn = this.querySelector('.gallery-zoom__prev');
        this.nextBtn = this.querySelector('.gallery-zoom__next');
        this.closeBtn = this.querySelector('.gallery-zoom__close');
        this.counterCurrent = this.querySelector('.media-ctrl__current-item');
        this.counterTotal = this.querySelector('.media-ctrl__total-items');
        this.mediaGallery = document.querySelector('.cc-main-product media-gallery');
        this.thumbSlider = this.querySelector('.gallery-zoom__thumb-slider');
        this.mobileSlides = [];

        // consts
        this.wheelZoomMultiplier = -0.001;
        this.pinchZoomMultiplier = 0.003;
        this.touchPanModifier = 1.0;
        this.doubleTapZoomMultiplier = 2.15;
        this.maxDoubleTapZoom = 0.88;
        this.swipeThreshold = 48;
        this.swipeAxisRatio = 1.2;

        // vars
        this.currentZoomImage = null;
        this.currentTransform = {
          panX: 0,
          panY: 0,
          zoom: 1
        };
        this.pinchTracking = {
          isTracking: false,
          lastPinchDistance: 0
        };
        this.touchTracking = {
          isTracking: false,
          lastTouchX: 0,
          lastTouchY: 0
        };
        this.lastTap = {
          time: 0,
          x: 0,
          y: 0
        };
        this.swipeTracking = {
          startX: 0,
          startY: 0,
          startScrollLeft: 0,
          lastX: 0,
          lastY: 0,
          hasMoved: false
        };
        this.boundStopControlEvent = this.stopControlEvent.bind(this);
        this.boundHandleCloseControl = this.handleCloseControl.bind(this);

        // events
        this.querySelectorAll('.gallery-zoom__thumb').forEach((el) => {
          el.addEventListener('click', this.onThumbClick.bind(this));
        });
        this.addEventListener('touchend', this.stopTrackingTouch.bind(this));
        this.addEventListener('mousemove', this.trackInputMovement.bind(this));
        this.addEventListener('wheel', this.trackWheel.bind(this));
        // prevent pan while swiping thumbnails
        this.thumbContainer.addEventListener('touchmove', (evt) => evt.stopPropagation());
        if (this.thumbSlider) {
          this.thumbSlider.addEventListener('touchstart', this.boundStopControlEvent, { passive: true });
          this.thumbSlider.addEventListener('touchmove', this.boundStopControlEvent, { passive: true });
          this.thumbSlider.addEventListener('touchend', this.boundStopControlEvent, { passive: true });
        }
        this.previousBtn.addEventListener('click', this.selectPreviousThumb.bind(this));
        this.nextBtn.addEventListener('click', this.selectNextThumb.bind(this));
        if (this.closeBtn) {
          this.closeBtn.addEventListener('touchstart', this.boundStopControlEvent, { passive: false });
          this.closeBtn.addEventListener('touchmove', this.boundStopControlEvent, { passive: false });
          this.closeBtn.addEventListener('touchend', this.boundHandleCloseControl, { passive: false });
          this.closeBtn.addEventListener('click', this.boundHandleCloseControl);
        }
        this.zoomContainer.addEventListener('click', this.onZoomContainerClick.bind(this));
        this.zoomContainer.addEventListener('dblclick', this.onZoomContainerDblClick.bind(this));
        this.zoomContainer.addEventListener('scroll', this.onZoomContainerScroll.bind(this), { passive: true });
        this.zoomContainer.addEventListener('touchstart', this.onZoomContainerTouchStart.bind(this), { passive: false });
        this.zoomContainer.addEventListener('touchmove', this.trackInputMovement.bind(this), { passive: false });
        this.zoomContainer.addEventListener('touchend', this.onZoomContainerTouchEnd.bind(this), { passive: false });
        new ResizeObserver(() => this.setInitialImagePosition()).observe(this);
      }

      document.documentElement.classList.add('gallery-zoom-open');
      this.observeModalState();
      this.addEventListener('keyup', this.handleKeyup.bind(this));
      setTimeout(() => this.classList.remove('gallery-zoom--pre-reveal'), 10);
    }

    // eslint-disable-next-line class-methods-use-this
    disconnectedCallback() {
      document.documentElement.classList.remove('gallery-zoom-open');
      clearTimeout(this.mobileReelScrollTimer);
      if (this.modalObserver) this.modalObserver.disconnect();
    }

    /**
     * Helper for creating a DOM element.
     * @param {string} type - Element type.
     * @param {string} className - Class to apply to element.
     * @param {Element} appendTo - Element to add to as a child.
     * @param {string} innerHTML - Inner HTML to apply to element.
     * @returns {Element} - The element created.
     */
    static createEl(type, className, appendTo, innerHTML) {
      const el = document.createElement(type);
      el.className = className;
      if (appendTo) {
        appendTo.insertAdjacentElement('beforeend', el);
      }
      if (innerHTML) {
        el.innerHTML = innerHTML;
      }
      return el;
    }

    /**
     * Returns the visible frame used for zoom positioning.
     * @returns {Element}
     */
    getZoomFrame() {
      if (this.isMobileZoom() && this.currentSlide) return this.currentSlide;
      return this.zoomContainer || this;
    }

    /**
     * Builds the mobile modal image reel so swiping uses native scroll physics.
     * @param {Element} activeThumb - Thumbnail to make active.
     */
    setupMobileReel(activeThumb) {
      const visibleThumbs = Array.from(this.thumbContainer.children).filter((thumb) => !thumb.hidden);
      const initialThumb = visibleThumbs.includes(activeThumb) ? activeThumb : visibleThumbs[0];

      this.zoomContainer.classList.add('gallery-zoom__zoom-container--reel');
      this.zoomContainer.classList.remove('gallery-zoom__zoom-container--is-zoomed', 'gallery-zoom__zoom-container--loading');
      this.zoomContainer.replaceChildren();
      this.currentSlide = null;
      this.mobileSlides = visibleThumbs.map((thumb, index) => {
        const slide = GalleryZoom.createEl('div', 'gallery-zoom__slide', this.zoomContainer);
        slide.dataset.mediaId = thumb.dataset.mediaId;

        const image = GalleryZoom.createEl('img', 'gallery-zoom__zoom-image', slide);
        image.alt = '';
        image.loading = thumb === initialThumb || index <= 1 ? 'eager' : 'lazy';
        image.decoding = 'async';
        image.src = thumb.dataset.zoomUrl;
        image.onload = () => {
          if (slide === this.currentSlide) this.setInitialImagePosition();
        };

        return slide;
      });

      this.selectThumb(initialThumb, { scrollReel: false });
      requestAnimationFrame(() => {
        if (this.currentSlide) this.jumpMobileReelToSlide(this.currentSlide);
      });
    }

    /**
     * Returns the scroll offset for a mobile reel slide.
     * @param {Element} slide - Mobile slide element.
     * @returns {number}
     */
    getMobileSlideOffset(slide) {
      if (!slide) return 0;

      const slideIndex = this.mobileSlides.indexOf(slide);
      if (slideIndex < 0) return slide.offsetLeft;

      return slideIndex * (this.zoomContainer.clientWidth || slide.clientWidth || 0);
    }

    /**
     * Jumps the mobile reel to a complete slide without scroll-snap fighting the correction.
     * @param {Element} slide - Mobile slide element.
     */
    jumpMobileReelToSlide(slide) {
      if (!slide || !this.zoomContainer) return;

      const slideOffset = this.getMobileSlideOffset(slide);
      const previousSnapType = this.zoomContainer.style.scrollSnapType;
      this.zoomContainer.style.scrollSnapType = 'none';
      this.zoomContainer.scrollTo({ left: slideOffset, behavior: 'auto' });
      this.zoomContainer.scrollLeft = slideOffset;

      requestAnimationFrame(() => {
        this.zoomContainer.scrollLeft = slideOffset;
        this.zoomContainer.style.scrollSnapType = previousSnapType;
      });
    }

    init(currentMediaId) {
      document.documentElement.classList.add('gallery-zoom-open');
      this.observeModalState();

      // Toggle thumbnail visibility based on the current state of media grouping
      if (this.mediaGallery.dataset.mediaGroupingEnabled) {
        let visibleThumbCount = 0;
        Array.from(this.thumbContainer.children).forEach((thumb) => {
          const mediaGalleryImage = this.mediaGallery.querySelector(`.media-viewer__item[data-media-id="${thumb.dataset.mediaId}"]`);
          thumb.hidden = mediaGalleryImage?.style.display === 'none';
          if (!thumb.hidden) visibleThumbCount += 1;
        });
        if (this.counterTotal) this.counterTotal.textContent = visibleThumbCount;
      }

      const activeThumb = [...this.thumbContainer.children].find((el) => el.dataset.mediaId === currentMediaId)
        || this.thumbContainer.firstElementChild;

      if (this.isMobileZoom()) {
        this.setupMobileReel(activeThumb);
      } else {
        this.zoomContainer.classList.remove('gallery-zoom__zoom-container--reel', 'gallery-zoom__zoom-container--is-zoomed');
        this.selectThumb(activeThumb);
      }
    }

    /**
     * Update pan values based on input event coordinates.
     * @param {number} inputX - Mouse/touch input X.
     * @param {number} inputY - Mouse/touch input Y.
     */
    panZoomImageFromCoordinate(inputX, inputY) {
      if (!this.currentZoomImage || !this.currentZoomImage.naturalWidth) return;

      const zoomFrame = this.getZoomFrame();
      const frameRect = zoomFrame.getBoundingClientRect();
      const localX = inputX - frameRect.left;
      const localY = inputY - frameRect.top;
      const zoomedWidth = this.currentZoomImage.naturalWidth * this.currentTransform.zoom;
      const zoomedHeight = this.currentZoomImage.naturalHeight * this.currentTransform.zoom;

      // do nothing if the image fits, pan if not
      const doPanX = zoomedWidth > zoomFrame.clientWidth;
      const doPanY = zoomedHeight > zoomFrame.clientHeight;

      if (doPanX || doPanY) {
        const midX = zoomFrame.clientWidth / 2;
        const midY = zoomFrame.clientHeight / 2;

        const offsetFromCentreX = localX - midX;
        const offsetFromCentreY = localY - midY;

        // the offsetMultipler ensures it can only pan to the edge of the image, no further
        let finalOffsetX = 0;
        let finalOffsetY = 0;

        if (doPanX) {
          const offsetMultiplierX = ((zoomedWidth - zoomFrame.clientWidth) / 2) / midX;
          finalOffsetX = Math.round(-offsetFromCentreX * offsetMultiplierX);
        }
        if (doPanY) {
          const offsetMultiplierY = ((zoomedHeight - zoomFrame.clientHeight) / 2) / midY;
          finalOffsetY = Math.round(-offsetFromCentreY * offsetMultiplierY);
        }

        this.currentTransform.panX = finalOffsetX;
        this.currentTransform.panY = finalOffsetY;
        this.alterCurrentPanBy(0, 0); // sanitise
        this.updateImagePosition();
      }
    }

    /**
     * Update pan values by a delta.
     * @param {number} x - Distance to pan X.
     * @param {number} y - Distance to pan Y.
     */
    alterCurrentPanBy(x, y) {
      if (!this.currentZoomImage || !this.currentZoomImage.naturalWidth) return;

      const zoomFrame = this.getZoomFrame();

      this.currentTransform.panX += x;
      // limit offset to keep most of image on screen
      let panXMax = (this.currentZoomImage.naturalWidth * this.currentTransform.zoom - zoomFrame.clientWidth) / 2.0;
      panXMax = Math.max(panXMax, 0);
      this.currentTransform.panX = Math.min(this.currentTransform.panX, panXMax);
      this.currentTransform.panX = Math.max(this.currentTransform.panX, -panXMax);

      this.currentTransform.panY += y;
      let panYMax = (this.currentZoomImage.naturalHeight * this.currentTransform.zoom - zoomFrame.clientHeight) / 2.0;
      panYMax = Math.max(panYMax, 0);
      this.currentTransform.panY = Math.min(this.currentTransform.panY, panYMax);
      this.currentTransform.panY = Math.max(this.currentTransform.panY, -panYMax);
      this.updateImagePosition();
    }

    /**
     * Set current zoom image transform to specific values.
     * @param {number} panX - Pan X value.
     * @param {number} panY - Pan Y value.
     * @param {number} zoom - Current zoom amount.
     */
    setCurrentTransform(panX, panY, zoom) {
      this.currentTransform.panX = panX;
      this.currentTransform.panY = panY;
      this.currentTransform.zoom = zoom;
      this.alterCurrentTransformZoomBy(0);
    }

    /**
     * Update zoom amount by a delta.
     * @param {number} delta - Amount to adjust.
     */
    alterCurrentTransformZoomBy(delta) {
      if (!this.currentZoomImage || !this.currentZoomImage.naturalWidth) return;

      this.currentTransform.zoom += delta;
      // do not zoom out further than fit
      const zoomFrame = this.getZoomFrame();
      const maxZoomX = zoomFrame.clientWidth / this.currentZoomImage.naturalWidth;
      const maxZoomY = zoomFrame.clientHeight / this.currentZoomImage.naturalHeight;
      this.currentTransform.zoom = Math.max(this.currentTransform.zoom, Math.min(maxZoomX, maxZoomY));

      // do not zoom in further than native size
      this.currentTransform.zoom = Math.min(this.currentTransform.zoom, 1.0);

      // reasses pan bounds
      this.alterCurrentPanBy(0, 0);
      this.updateImagePosition();
    }

    /**
     * Position the current image in the centre, zoomed out
     */
    setInitialImagePosition() {
      if (!this.currentZoomImage || !this.currentZoomImage.naturalWidth) return;

      const zoomFrame = this.getZoomFrame();
      const top = `${zoomFrame.clientHeight / 2 - this.currentZoomImage.clientHeight / 2}px`;
      const left = `${zoomFrame.clientWidth / 2 - this.currentZoomImage.clientWidth / 2}px`;

      this.currentZoomImage.style.top = top;
      this.currentZoomImage.style.left = left;
      this.currentZoomImage.style.setProperty('--gallery-zoom-top', top);
      this.currentZoomImage.style.setProperty('--gallery-zoom-left', left);
      this.setCurrentTransform(0, 0, 0);
    }

    /**
     * Centers the native-size image in the mobile zoom frame without panning.
     */
    centerMobileZoomedImage() {
      if (!this.currentZoomImage || !this.currentZoomImage.naturalWidth) return;

      const zoomFrame = this.getZoomFrame();
      const imageWidth = this.currentZoomImage.naturalWidth || this.currentZoomImage.clientWidth;
      const imageHeight = this.currentZoomImage.naturalHeight || this.currentZoomImage.clientHeight;
      const top = `${zoomFrame.clientHeight / 2 - imageHeight / 2}px`;
      const left = `${zoomFrame.clientWidth / 2 - imageWidth / 2}px`;

      this.currentZoomImage.style.top = top;
      this.currentZoomImage.style.left = left;
      this.currentZoomImage.style.setProperty('--gallery-zoom-top', top);
      this.currentZoomImage.style.setProperty('--gallery-zoom-left', left);
      this.currentTransform.panX = 0;
      this.currentTransform.panY = 0;
      this.updateImagePosition();
    }

    /**
     * Set current zoom image transform based on pan & zoom values.
     */
    updateImagePosition() {
      if (!this.currentZoomImage) return;

      const transform = `translate3d(${this.currentTransform.panX}px, ${this.currentTransform.panY}px, 0) scale(${this.currentTransform.zoom})`;
      this.currentZoomImage.style.transform = transform;
      this.currentZoomImage.style.setProperty('--gallery-zoom-transform', transform);
    }

    /**
     * Select a thumbnail.
     * @param {Element} thumb - Thumbnail to select.
     * @param {object} options - Selection options.
     */
    selectThumb(thumb, options = {}) {
      if (!thumb) return;

      const visibleThumbs = Array.from(thumb.parentElement.children).filter((child) => !child.hidden);
      const activeThumb = visibleThumbs.includes(thumb) ? thumb : visibleThumbs[0];
      if (!activeThumb) return;

      [...thumb.parentElement.children].forEach((el) => {
        if (el === activeThumb) {
          el.classList.add('gallery-zoom__thumb--active');
        } else {
          el.classList.remove('gallery-zoom__thumb--active');
        }
      });

      const activeThumbIndex = visibleThumbs.indexOf(activeThumb);
      if (this.counterCurrent) this.counterCurrent.textContent = activeThumbIndex + 1;

      // If the thumb isn't visible, scroll to it
      this.scrollToThumb(activeThumb);

      const wrapControls = this.isMobileZoom();
      this.previousBtn.disabled = !wrapControls && activeThumbIndex === 0;
      this.nextBtn.disabled = !wrapControls && (activeThumbIndex === (visibleThumbs.length - 1));

      if (this.isMobileZoom() && this.mobileSlides.length > 0) {
        const activeSlide = this.mobileSlides.find((slide) => slide.dataset.mediaId === activeThumb.dataset.mediaId);
        if (!activeSlide) return;

        this.currentSlide = activeSlide;
        this.mobileSlides.forEach((slide) => {
          slide.classList.toggle('is-active', slide === activeSlide);
        });
        this.currentZoomImage = activeSlide.querySelector('.gallery-zoom__zoom-image');
        this.zoomContainer.classList.remove('gallery-zoom__zoom-container--loading', 'gallery-zoom__zoom-container--is-zoomed');
        if (this.currentZoomImage) this.currentZoomImage.classList.remove('is-zoomed');

        if (options.scrollReel !== false) {
          this.zoomContainer.scrollTo({
            left: this.getMobileSlideOffset(activeSlide),
            behavior: options.behavior || 'smooth'
          });
        }

        if (this.currentZoomImage && this.currentZoomImage.complete) this.setInitialImagePosition();
        return;
      }

      // replace zoom image
      this.zoomContainer.classList.add('gallery-zoom__zoom-container--loading');
      this.currentZoomImage = GalleryZoom.createEl('img', 'gallery-zoom__zoom-image');
      this.currentZoomImage.alt = '';
      this.currentZoomImage.style.visibility = 'hidden';
      this.currentZoomImage.onload = () => {
        this.zoomContainer.classList.remove('gallery-zoom__zoom-container--loading');
        this.currentZoomImage.style.visibility = '';
        this.setInitialImagePosition();
      };
      this.currentZoomImage.src = thumb.dataset.zoomUrl;
      this.zoomContainer.replaceChildren(this.currentZoomImage);
    }

    /**
     * Scrolls the thumbnail slider to the active thumbnail if necessary
     * @param {Element} thumb - Thumbnail to scroll to.
     */
    scrollToThumb(thumb) {
      const thumbSliderRect = this.thumbSlider.getBoundingClientRect();
      const thumbRect = thumb.getBoundingClientRect();

      const thumbLeft = thumbRect.left - thumbSliderRect.left;
      const thumbRight = thumbRect.right - thumbSliderRect.right;

      if (thumbLeft < 0) {
        this.thumbSlider.scrollLeft += thumbLeft - (this.thumbSlider.clientWidth - thumbRect.width);
      } else if (thumbRight > 0) {
        this.thumbSlider.scrollLeft += thumbRight + (this.thumbSlider.clientWidth - thumbRect.width);
      }
    }

    /**
     * Syncs active controls after native mobile reel scrolling settles.
     */
    onZoomContainerScroll() {
      if (!this.isMobileZoom() || this.mobileSlides.length === 0 || this.isImageZoomed()) return;

      clearTimeout(this.mobileReelScrollTimer);
      this.mobileReelScrollTimer = setTimeout(() => this.settleMobileReel(), 140);
    }

    /**
     * Snaps the mobile reel to a complete slide after native scroll settles.
     */
    settleMobileReel() {
      if (!this.isMobileZoom() || this.mobileSlides.length === 0 || this.isImageZoomed()) return;

      const slideWidth = this.zoomContainer.clientWidth || 1;
      const index = Math.min(
        Math.max(Math.round(this.zoomContainer.scrollLeft / slideWidth), 0),
        this.mobileSlides.length - 1
      );
      const slide = this.mobileSlides[index];
      const thumb = Array.from(this.thumbContainer.children).find((item) => item.dataset.mediaId === slide?.dataset.mediaId);
      if (!slide || !thumb) return;

      if (slide !== this.currentSlide) {
        this.selectThumb(thumb, { scrollReel: false });
      }
    }

    /**
     * Watches modal open state because the gallery element remains connected after close.
     */
    observeModalState() {
      const modal = this.closest('modal-dialog');

      if (!modal || this.modalObserver) return;

      this.modalObserver = new MutationObserver(() => {
        if (!modal.hasAttribute('open')) this.resetAfterClose();
      });
      this.modalObserver.observe(modal, { attributes: true, attributeFilter: ['open'] });
    }

    /**
     * Prevent gallery gestures from consuming control and thumbnail touches.
     * @param {object} evt - Event object.
     */
    stopControlEvent(evt) {
      evt.stopPropagation();
    }

    /**
     * Close the gallery from the mobile Back control.
     * @param {object} evt - Event object.
     */
    handleCloseControl(evt) {
      evt.preventDefault();
      evt.stopPropagation();
      this.closeGallery();
    }

    /**
     * Reset gallery state after the modal closes.
     */
    resetAfterClose() {
      this.stopTrackingTouch();
      this.lastTap.time = 0;
      clearTimeout(this.mobileReelScrollTimer);
      document.documentElement.classList.remove('gallery-zoom-open');
    }

    /**
     * Close the containing modal and restore page scroll state.
     */
    closeGallery() {
      const modal = this.closest('modal-dialog');
      this.resetAfterClose();

      if (modal && typeof modal.close === 'function' && modal.hasAttribute('open')) {
        modal.close();
        return;
      }

      if (modal) modal.removeAttribute('open');
      document.body.style.top = '';
      document.body.classList.remove('fixed');
    }

    /**
     * Select previous thumbnail.
     * @param {Event} evt - Click event, if triggered by pagination.
     */
    selectPreviousThumb(evt) {
      if (evt) evt.preventDefault();
      if (this.thumbContainer.childElementCount < 2) return;

      if (this.isMobileZoom() && this.mobileSlides.length > 0) {
        const visibleThumbs = Array.from(this.thumbContainer.children).filter((thumb) => !thumb.hidden);
        const activeThumb = this.thumbContainer.querySelector('.gallery-zoom__thumb--active');
        const activeIndex = visibleThumbs.indexOf(activeThumb);
        if (activeIndex > 0) this.selectThumb(visibleThumbs[activeIndex - 1]);
        return;
      }

      let previous = this.thumbContainer.querySelector('.gallery-zoom__thumb--active').previousElementSibling;
      while (previous && !previous.offsetParent) {
        previous = previous.previousElementSibling;
      }
      if (!previous) {
        if (this.isMobileZoom()) return;
        previous = this.thumbContainer.lastElementChild;
      }
      while (previous && !previous.offsetParent) {
        previous = previous.previousElementSibling;
      }
      if (!previous) return;

      this.selectThumb(previous);
    }

    /**
     * Select next thumbnail.
     * @param {Event} evt - Click event, if triggered by pagination.
     */
    selectNextThumb(evt) {
      if (evt) evt.preventDefault();
      if (this.thumbContainer.childElementCount < 2) return;

      if (this.isMobileZoom() && this.mobileSlides.length > 0) {
        const visibleThumbs = Array.from(this.thumbContainer.children).filter((thumb) => !thumb.hidden);
        const activeThumb = this.thumbContainer.querySelector('.gallery-zoom__thumb--active');
        const activeIndex = visibleThumbs.indexOf(activeThumb);
        if (activeIndex < visibleThumbs.length - 1) this.selectThumb(visibleThumbs[activeIndex + 1]);
        return;
      }

      let next = this.thumbContainer.querySelector('.gallery-zoom__thumb--active').nextElementSibling;
      while (next && !next.offsetParent) {
        next = next.nextElementSibling;
      }
      if (!next) {
        if (this.isMobileZoom()) return;
        next = this.thumbContainer.firstElementChild;
      }
      while (next && !next.offsetParent) {
        next = next.nextElementSibling;
      }
      if (!next) return;

      this.selectThumb(next);
    }

    /**
     * Call to stop tracking touch events.
     */
    stopTrackingTouch() {
      this.pinchTracking.isTracking = false;
      this.touchTracking.isTracking = false;
    }

    /**
     * Start tracking mobile swipe/pan intent inside the image area.
     * @param {object} evt - Event object.
     */
    onZoomContainerTouchStart(evt) {
      if (!this.isMobileZoom() || evt.touches.length !== 1) return;

      const touch = evt.touches[0];
      this.swipeTracking = {
        startX: touch.clientX,
        startY: touch.clientY,
        startScrollLeft: this.zoomContainer.scrollLeft,
        lastX: touch.clientX,
        lastY: touch.clientY,
        hasMoved: false
      };
      this.touchTracking.isTracking = true;
      this.touchTracking.lastTouchX = touch.clientX;
      this.touchTracking.lastTouchY = touch.clientY;
    }

    /**
     * Handle mouse and touch events.
     * @param {object} evt - Event object.
     */
    trackInputMovement(evt) {
      if (evt.type === 'touchmove' && evt.touches.length > 0) {
        const touch1 = evt.touches[0];
        this.swipeTracking.lastX = touch1.clientX;
        this.swipeTracking.lastY = touch1.clientY;

        if (Math.abs(touch1.clientX - this.swipeTracking.startX) > 6 || Math.abs(touch1.clientY - this.swipeTracking.startY) > 6) {
          this.swipeTracking.hasMoved = true;
        }

        if (evt.touches.length === 1 && this.isMobileZoom() && !this.isImageZoomed()) {
          this.touchTracking.lastTouchX = touch1.clientX;
          this.touchTracking.lastTouchY = touch1.clientY;
          return;
        }

        evt.preventDefault();

        // pan
        if (!this.touchTracking.isTracking) {
          this.touchTracking.isTracking = true;
          this.touchTracking.lastTouchX = touch1.clientX;
          this.touchTracking.lastTouchY = touch1.clientY;
        } else {
          this.alterCurrentPanBy(
            (touch1.clientX - this.touchTracking.lastTouchX) * this.touchPanModifier,
            (touch1.clientY - this.touchTracking.lastTouchY) * this.touchPanModifier
          );
          this.touchTracking.lastTouchX = touch1.clientX;
          this.touchTracking.lastTouchY = touch1.clientY;
        }

        if (evt.touches.length === 2) {
          // pinch
          const touch2 = evt.touches[1];
          const pinchDistance = Math.sqrt((touch1.clientX - touch2.clientX) ** 2 + (touch1.clientY - touch2.clientY) ** 2);
          if (!this.pinchTracking.isTracking) {
            this.pinchTracking.lastPinchDistance = pinchDistance;
            this.pinchTracking.isTracking = true;
          } else {
            const pinchDelta = pinchDistance - this.pinchTracking.lastPinchDistance;
            this.alterCurrentTransformZoomBy(pinchDelta * this.pinchZoomMultiplier);
            this.pinchTracking.lastPinchDistance = pinchDistance;
          }
        } else {
          this.pinchTracking.isTracking = false;
        }
      } else {
        // mousemove
        this.panZoomImageFromCoordinate(evt.clientX, evt.clientY);
      }
    }

    /**
     * Handle mouse wheel scroll - use for zoom.
     * @param {object} evt - Event object.
     */
    trackWheel(evt) {
      evt.preventDefault();
      if (evt.deltaY !== 0) {
        this.alterCurrentTransformZoomBy(evt.deltaY * this.wheelZoomMultiplier);
        this.syncZoomedStateClasses();
      }
    }

    /**
     * Keep the is-zoomed state classes in sync after a continuous (wheel) zoom. toggleZoomAt sets these for
     * click/double-tap, but wheel changes the zoom incrementally, so without this the grab cursor + container
     * "is-zoomed" styling desync when scroll-zooming.
     */
    syncZoomedStateClasses() {
      if (!this.currentZoomImage) return;
      const zoomed = this.isImageZoomed();
      this.currentZoomImage.classList.toggle('is-zoomed', zoomed);
      this.zoomContainer.classList.toggle('gallery-zoom__zoom-container--is-zoomed', zoomed);
    }

    /**
     * Handle click on a thumbnail image.
     * @param {object} evt - Event object.
     */
    onThumbClick(evt) {
      evt.preventDefault();
      this.selectThumb(evt.currentTarget);
    }

    /**
     * Handle click on the main zoom image. (Toggle zoom in/out.)
     * @param {object} evt - Event object.
     */
    onZoomContainerClick(evt) {
      evt.preventDefault();

      if (this.isMobileZoom()) return;

      this.toggleZoomAt(evt.clientX, evt.clientY);
    }

    /**
     * Toggle desktop zoom in/out at the double-clicked point (in addition to single click).
     * @param {MouseEvent} evt - Double-click event.
     */
    onZoomContainerDblClick(evt) {
      evt.preventDefault();

      if (this.isMobileZoom()) return;

      // A dblclick is preceded by two single clicks (toggle on → off); re-assert zoomed-in.
      if (!this.isImageZoomed()) this.toggleZoomAt(evt.clientX, evt.clientY);
    }

    /**
     * Select adjacent media from a horizontal mobile swipe.
     * @param {number} deltaX - Swipe X distance.
     */
    selectThumbFromSwipe(deltaX) {
      if (Math.abs(deltaX) < this.swipeThreshold) return false;

      if (deltaX < 0) {
        this.selectNextThumb();
      } else {
        this.selectPreviousThumb();
      }
      return true;
    }

    /**
     * Handle mobile double tap on the main zoom image.
     * @param {object} evt - Event object.
     */
    onZoomContainerTouchEnd(evt) {
      if (!this.isMobileZoom() || evt.changedTouches.length !== 1) return;

      const touch = evt.changedTouches[0];
      const swipeDeltaX = touch.clientX - this.swipeTracking.startX;
      const swipeDeltaY = touch.clientY - this.swipeTracking.startY;
      const isHorizontalSwipe = Math.abs(swipeDeltaX) > Math.abs(swipeDeltaY) * this.swipeAxisRatio;
      const now = Date.now();
      const elapsed = now - this.lastTap.time;
      const deltaX = touch.clientX - this.lastTap.x;
      const deltaY = touch.clientY - this.lastTap.y;
      const distance = Math.sqrt(deltaX ** 2 + deltaY ** 2);
      const isDoubleTap = elapsed > 0 && elapsed < 320 && distance < 28;

      if (this.swipeTracking.hasMoved) {
        if (!this.isImageZoomed()) {
          if (isHorizontalSwipe && Math.abs(swipeDeltaX) >= this.swipeThreshold) this.settleMobileReel();
        }
        this.lastTap.time = 0;
        this.stopTrackingTouch();
        return;
      }

      if (isDoubleTap) {
        evt.preventDefault();
        this.toggleZoomAt(touch.clientX, touch.clientY);
        this.lastTap.time = 0;
        this.stopTrackingTouch();
        return;
      }

      this.lastTap = {
        time: now,
        x: touch.clientX,
        y: touch.clientY
      };
    }

    /**
     * Toggle the current image between fit and native zoom.
     * @param {number} clientX - Input X coordinate.
     * @param {number} clientY - Input Y coordinate.
     */
    toggleZoomAt(clientX, clientY) {
      if (!this.currentZoomImage || !this.currentZoomImage.naturalWidth) return;

      if (this.isImageZoomed()) {
        this.currentTransform.zoom = 0;
        this.alterCurrentTransformZoomBy(0);
        this.currentZoomImage.classList.remove('is-zoomed');
        this.zoomContainer.classList.remove('gallery-zoom__zoom-container--is-zoomed');
        if (this.currentSlide) this.jumpMobileReelToSlide(this.currentSlide);
      } else {
        if (this.currentSlide) this.jumpMobileReelToSlide(this.currentSlide);
        if (this.isMobileZoom()) {
          this.currentZoomImage.classList.add('is-zoomed');
          this.zoomContainer.classList.add('gallery-zoom__zoom-container--is-zoomed');
          this.currentTransform.zoom = this.getDoubleTapZoom();
          this.alterCurrentTransformZoomBy(0);
          this.centerMobileZoomedImage();
          return;
        }

        this.setInitialImagePosition();
        this.currentTransform.zoom = this.getDoubleTapZoom();
        this.alterCurrentTransformZoomBy(0);
        this.panZoomImageFromCoordinate(clientX, clientY);
        this.currentZoomImage.classList.add('is-zoomed');
        this.zoomContainer.classList.add('gallery-zoom__zoom-container--is-zoomed');
      }
    }

    /**
     * Returns the fit-to-screen zoom value.
     * @returns {number}
     */
    getFitZoom() {
      if (!this.currentZoomImage || !this.currentZoomImage.naturalWidth || !this.currentZoomImage.naturalHeight) return 1;

      const zoomFrame = this.getZoomFrame();
      const fitZoomX = zoomFrame.clientWidth / this.currentZoomImage.naturalWidth;
      const fitZoomY = zoomFrame.clientHeight / this.currentZoomImage.naturalHeight;
      return Math.min(fitZoomX, fitZoomY, 1);
    }

    /**
     * Returns the reduced mobile double-tap zoom value.
     * @returns {number}
     */
    getDoubleTapZoom() {
      const fitZoom = this.getFitZoom();
      return Math.min(1, Math.max(fitZoom, Math.min(this.maxDoubleTapZoom, fitZoom * this.doubleTapZoomMultiplier)));
    }

    /**
     * Returns whether the current image is zoomed beyond fit-to-screen.
     * @returns {boolean}
     */
    isImageZoomed() {
      if (!this.currentZoomImage || !this.currentZoomImage.naturalWidth) return false;
      return this.currentTransform.zoom > this.getFitZoom() + 0.05;
    }

    /**
     * Returns whether the lightbox is currently using mobile gestures.
     * @returns {boolean}
     */
    isMobileZoom() {
      return window.matchMedia('(max-width: 768.98px)').matches;
    }

    /**
     * Handle key events.
     * @param {object} evt - Event object.
     */
    handleKeyup(evt) {
      switch (evt.key) {
        case 'ArrowLeft':
          evt.preventDefault();
          this.selectPreviousThumb();
          break;
        case 'ArrowRight':
          evt.preventDefault();
          this.selectNextThumb();
          break;
      }
    }
  }

  window.customElements.define('gallery-zoom', GalleryZoom);
}
